import type { GmLearner, StreakProgress, GmMe, GmCard } from './types';

/**
 * Pure logic for the SSC Rankers League flow (no React, no network): which stage the learner is in,
 * how the streak reads, countdowns and number formatting. Everything here is unit-tested.
 */

/** Where the learner stands. The screen picks its panel from this; the server enforces it all. */
export type Stage =
  | { kind: 'none' }
  | { kind: 'cancelled'; reason: string | null }
  /** An attempt is running: resume it (there is no pause and no restart). */
  | { kind: 'in_progress'; attemptId: string }
  /** Submitted (by the learner or by the server at time up); results are not out yet. */
  | { kind: 'submitted'; attemptId: string }
  | { kind: 'results'; attemptId: string | null }
  | { kind: 'not_eligible' }
  | { kind: 'upcoming' }
  | { kind: 'ready' }
  /** Live but the team paused entry (an emergency); starting is blocked until it resumes. */
  | { kind: 'paused' }
  /** The exam is running or over and this learner has no attempt (entry window closed). */
  | { kind: 'missed' };

export function deriveStage(dto: GmLearner | null, nowMs: number): Stage {
  const gm = dto?.grandMock;
  if (!gm) return { kind: 'none' };
  if (gm.status === 'cancelled') return { kind: 'cancelled', reason: gm.cancelReason };
  const me = dto.me;
  if (me?.attempt) {
    if (me.attempt.status === 'in_progress' && !me.resultsOut)
      return { kind: 'in_progress', attemptId: me.attempt.id };
    return me.resultsOut
      ? { kind: 'results', attemptId: me.attempt.id }
      : { kind: 'submitted', attemptId: me.attempt.id };
  }
  if (me?.resultsOut || gm.status === 'results_published' || gm.status === 'paid')
    return { kind: 'results', attemptId: null };
  if (me && !me.eligibility.eligible && gm.status !== 'ended') return { kind: 'not_eligible' };
  if (gm.status === 'scheduled' || gm.status === 'draft') return { kind: 'upcoming' };
  if (gm.status === 'live') {
    if (me?.canStart) return { kind: 'ready' };
    if (gm.paused && nowMs < Date.parse(gm.joinClosesAt)) return { kind: 'paused' };
    return { kind: 'missed' };
  }
  return { kind: 'missed' };
}

// ── Streak ("Streak 9/14") ──────────────────────────────────────────────────────────────────

/** Done days never read above the target, and never below 0. */
function streakCounts(s: Pick<StreakProgress, 'done' | 'required'>): {
  done: number;
  required: number;
} {
  const required = Math.max(0, Math.trunc(s.required));
  const done = Math.min(required, Math.max(0, Math.trunc(s.done)));
  return { done, required };
}

export const streakLabel = (s: Pick<StreakProgress, 'done' | 'required'>): string => {
  const { done, required } = streakCounts(s);
  return `Streak ${String(done)}/${String(required)}`;
};

export const streakPercent = (s: Pick<StreakProgress, 'done' | 'required'>): number => {
  const { done, required } = streakCounts(s);
  return required ? Math.round((done / required) * 100) : 0;
};

/** The streak to show for this learner: eligibility's own copy first, then `me.streak`. */
export function streakOf(me: GmMe | null): StreakProgress | null {
  if (!me) return null;
  if (!me.eligibility.eligible && me.eligibility.streak) return me.eligibility.streak;
  return me.streak;
}

export type HelpKind =
  'qualified' | 'pass' | 'streak_today' | 'streak_ok' | 'streak_broken' | 'needs_pass';

/** What the learner should do about eligibility (the page turns this into plain sentences). */
export function eligibilityHelp(me: GmMe | null): HelpKind | null {
  if (!me) return null;
  const e = me.eligibility;
  if (e.eligible) return e.via === 'pass' ? 'pass' : 'qualified';
  if (e.reason === 'streak_broken' || e.streak?.broken) return 'streak_broken';
  if (e.reason === 'streak_in_progress') return e.streak?.dueToday ? 'streak_today' : 'streak_ok';
  return 'needs_pass';
}

// ── Prizes ──────────────────────────────────────────────────────────────────────────────────

/** Prize wording is shown only when the API sends amounts (feature flag on). */
export const prizesVisible = (gm: Pick<GmCard, 'prizesInr'> | null): boolean =>
  !!gm?.prizesInr && gm.prizesInr.some((n) => n > 0);

const ORDINAL = ['1st', '2nd', '3rd'];

/** Prize amounts only (shown when prizes are on); never a plan price, which is Stars-only in Telegram. */
export const inr = (n: number): string => `\u20b9${new Intl.NumberFormat('en-IN').format(n)}`;

export const prizeLine = (prizes: number[]): string =>
  prizes
    .filter((n) => n > 0)
    .map((n, i) => `${ORDINAL[i] ?? `${String(i + 1)}th`} ${inr(n)}`)
    .join(' · ');

// ── Time ────────────────────────────────────────────────────────────────────────────────────

/** Client clock error against the server (add to `Date.now()` to get server time). */
export const clockOffset = (serverTimeIso: string, clientNowMs: number): number => {
  const s = Date.parse(serverTimeIso);
  return Number.isFinite(s) ? s - clientNowMs : 0;
};

/** "3 days 4 h", "2 h 10 min", "12 min", "Starting now". Whole minutes, never a ticking second. */
export function countdown(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 30_000) return 'Starting now';
  const mins = Math.max(1, Math.round(ms / 60_000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d >= 1) return `${String(d)} ${d === 1 ? 'day' : 'days'}${h ? ` ${String(h)} h` : ''}`;
  if (h >= 1) return m ? `${String(h)} h ${String(m)} min` : `${String(h)} h`;
  return `${String(m)} min`;
}

/** Fraction (0 to 1) or percentage to "86.5%". */
export function accuracyPct(a: number): string {
  const pct = a <= 1 ? a * 100 : a;
  const r = Math.round(pct * 10) / 10;
  return `${Number.isInteger(r) ? String(r) : r.toFixed(1)}%`;
}

// ── Navigation and errors ───────────────────────────────────────────────────────────────────

/** Deep-link names for the SSC Rankers League: the new names and the old ones (still accepted). */
const OPEN_PARAMS = new Set(['monthly_exam', 'monthly-exam', 'grand_mock', 'grand-mock']);

/** Open the SSC Rankers League directly from a bot button: `startapp=monthly_exam` or `?screen=monthly-exam` (old `grand_mock` links work too). */
export function opensGrandMock(startParam: string | null, search: string): boolean {
  if (OPEN_PARAMS.has(startParam ?? '')) return true;
  const q = new URLSearchParams(search);
  const fromLink = q.get('startapp') ?? q.get('tgWebAppStartParam');
  return OPEN_PARAMS.has(q.get('screen') ?? '') || OPEN_PARAMS.has(fromLink ?? '');
}

/** Plain-language message for an SSC Rankers League API error code. */
export function friendlyError(code: string, fallback: string): string {
  switch (code) {
    case 'grand_mock.not_eligible':
      return 'You are not eligible for this SSC Rankers League yet. Check the steps on this page.';
    case 'grand_mock.agree_first':
    case 'grand_mock.agree_all':
      return 'Please tick every box and accept the rules to continue.';
    case 'grand_mock.rules_changed':
      return 'The rules were updated. Please read them again and accept to continue.';
    case 'grand_mock.not_started':
      return 'The SSC Rankers League has not started yet. Please come back at the start time.';
    case 'grand_mock.closed':
      return 'Entry is closed for this SSC Rankers League.';
    case 'mock.already_attempted':
      return 'You have already taken this SSC Rankers League. Each student gets one attempt.';
    case 'mock.paused':
    case 'grand_mock.paused':
      return 'The exam is paused by the team. Please wait; your timer is handled by the server.';
    default:
      return fallback;
  }
}
